import { useMemo } from 'react';

import type { SelectItemType } from '@/lib/base/forms/select/select';
import type {
  AssociationType,
  CrmObjectTypeSchema,
} from '@/lib/crm/core/SchemaClient';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { encodeAssocPropKey } from '@/lib/crm/utils/assocPropKey';
import { getAssociationTargetIconComponent } from '@/lib/crm/utils/icons';
import { getPropertyTypeIconComponent } from '@/lib/crm/utils/icons/PropertyTypeIcon';

import { mergeAssociatedTargetProperties } from './mergeTargetProperties';
import type { FieldSection } from './shared';

/**
 * Builds the field combobox's sections: the source entity's own properties,
 * plus either a flattened per-association section (target record picker +
 * target properties, when `enableAssociatedPropertyFilters`) or a single
 * legacy "Associations" section of record-only items.
 */
export function useFilterFieldSections(
  filterablePropertyTypes: PropertyTypeSchema[],
  filterableAssociations: AssociationType[],
  enableAssociatedPropertyFilters: boolean,
  resolvedTargetTypes: Record<string, string[]>,
  targetFilterableProps: Record<string, PropertyTypeSchema[]>,
  objectTypeLookup: Record<string, CrmObjectTypeSchema>,
  targetFetchErrors: Set<string>
): FieldSection[] {
  return useMemo<FieldSection[]>(() => {
    const sections: FieldSection[] = [];

    // Source entity's own properties
    const propertyItems: SelectItemType[] = filterablePropertyTypes.map(
      prop => ({
        id: prop.property_type_name,
        label: prop.label,
        icon: getPropertyTypeIconComponent(prop),
      })
    );
    if (propertyItems.length > 0) {
      sections.push({
        key: 'properties',
        label: 'Properties',
        items: propertyItems,
      });
    }

    // Association sections — when enableAssociatedPropertyFilters is on, each
    // association gets its own section with a "Specific {type}" record-picker
    // item followed by the target entity's filterable properties. When off,
    // association items go into a single "Associations" section (legacy).
    if (enableAssociatedPropertyFilters) {
      for (const assoc of filterableAssociations) {
        const targetTypes =
          resolvedTargetTypes[assoc.association_type_name] ?? [];
        const firstTargetType = targetTypes[0];
        const mergedProps = mergeAssociatedTargetProperties(
          targetTypes,
          targetFilterableProps
        );
        const targetProps = Object.values(mergedProps).sort((a, b) =>
          a.label.localeCompare(b.label)
        );
        const targetSchema = firstTargetType
          ? objectTypeLookup[firstTargetType]
          : undefined;

        const sectionItems: SelectItemType[] = [];

        const isToMany =
          assoc.cardinality === 'one_to_many' ||
          assoc.cardinality === 'many_to_many';
        const typeLabel = isToMany
          ? (targetSchema?.label_plural ?? targetSchema?.label ?? 'record')
          : (targetSchema?.label ?? 'record');
        // Record-picker label. To-one associations are roles (Owner, Creator,
        // Stage) whose label is a singular noun — label the picker by the ROLE
        // ("Specific owner") so it's self-explanatory and two user-targeted
        // roles (Owner vs Creator) don't both collapse to an identical
        // "Specific user". To-many associations are collections often labelled
        // as phrases ("Associated Companies"), where "Specific {role}" reads
        // badly, so those keep the target-type plural ("Specific companies") —
        // their section header already carries the relationship.
        const recordPickerLabel = isToMany
          ? `Specific ${typeLabel.toLowerCase()}`
          : `Specific ${assoc.label.toLowerCase()}`;
        // The combobox filters on each item's textValue, not on section headers,
        // so without help an association is unfindable by its own name (e.g.
        // searching "owner" hid the "Owner" section entirely because its items
        // were "Specific user", "Name", … — none contain "owner"). Baking the
        // association label into each item's textValue makes the whole section
        // surface when its name is searched. textValue also drives the combobox's
        // selected-value display, so the record-picker displays as the
        // association label alone ("Owner"), while target-property items qualify
        // with it ("Owner Email") to disambiguate which record's property.
        sectionItems.push({
          id: `assoc:${assoc.association_type_name}`,
          label: recordPickerLabel,
          textValue: assoc.label,
          icon: getAssociationTargetIconComponent(assoc),
        });

        // Target entity properties (e.g. "Industry", "Company Type")
        if (
          targetTypes.length > 0 &&
          targetTypes.some(t => targetFetchErrors.has(t))
        ) {
          sectionItems.push({
            id: `error:${assoc.association_type_name}`,
            label: 'Properties unavailable',
            isDisabled: true,
          });
        } else {
          for (const prop of targetProps) {
            sectionItems.push({
              id: encodeAssocPropKey(
                assoc.association_type_name,
                prop.property_type_name
              ),
              label: prop.label,
              textValue: `${assoc.label} ${prop.label}`,
              icon: getPropertyTypeIconComponent(prop),
            });
          }
        }

        sections.push({
          key: `assoc:${assoc.association_type_name}`,
          label: assoc.label,
          items: sectionItems,
        });
      }
    } else {
      // Feature disabled — single "Associations" section with record-only items
      const assocItems: SelectItemType[] = filterableAssociations.map(
        assoc => ({
          id: `assoc:${assoc.association_type_name}`,
          label: assoc.label,
          icon: getAssociationTargetIconComponent(assoc),
        })
      );
      if (assocItems.length > 0) {
        sections.push({
          key: 'associations',
          label: 'Associations',
          items: assocItems,
        });
      }
    }

    return sections;
  }, [
    filterablePropertyTypes,
    filterableAssociations,
    enableAssociatedPropertyFilters,
    resolvedTargetTypes,
    targetFilterableProps,
    objectTypeLookup,
    targetFetchErrors,
  ]);
}
